import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BellIcon } from '@heroicons/react/24/outline';
import { useNotifications } from '../../hooks/useNotifications';
import styles from './NotificationBell.module.css';

const TYPE_LABELS = {
  news: 'NEWS',
  feature: 'FEAT',
  alert: 'ALERT',
  maintenance: 'MAINT',
  tip: 'TIP',
};

function timeAgo(dateStr, t) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return t('notifications.justNow');
  if (diffMin < 60) return t('notifications.minutesAgo', { count: diffMin });
  if (diffHr < 24) return t('notifications.hoursAgo', { count: diffHr });
  return t('notifications.daysAgo', { count: diffDay });
}

export default function NotificationBell() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);

  const {
    unreadCount,
    notifications,
    isLoadingNotifications,
    refetchNotifications,
    markRead,
    dismiss,
    markAllRead,
  } = useNotifications();

  const handleToggle = useCallback(() => {
    setOpen((prev) => {
      if (!prev) {
        refetchNotifications();
      }
      return !prev;
    });
  }, [refetchNotifications]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handleKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  const handleMarkRead = (id) => {
    markRead(id);
  };

  const handleDismiss = (e, id) => {
    e.stopPropagation();
    dismiss(id);
  };

  const handleMarkAllRead = () => {
    markAllRead();
  };

  return (
    <div className={styles.container}>
      <button
        ref={buttonRef}
        className={styles.bellButton}
        onClick={handleToggle}
        aria-label={t('notifications.title')}
      >
        <BellIcon className={styles.bellIcon} />
        {unreadCount > 0 && (
          <span className={styles.badge}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div ref={panelRef} className={styles.panel}>
          <div className={styles.panelHeader}>
            <span className={styles.panelTitle}>{t('notifications.title')}</span>
            {notifications.length > 0 && (
              <button
                className={styles.markAllButton}
                onClick={handleMarkAllRead}
              >
                {t('notifications.markAllRead')}
              </button>
            )}
          </div>

          <div className={styles.panelBody}>
            {isLoadingNotifications && (
              <div className={styles.emptyState}>{t('common.loading')}</div>
            )}
            {!isLoadingNotifications && notifications.length === 0 && (
              <div className={styles.emptyState}>{t('notifications.empty')}</div>
            )}
            {notifications.map((n) => (
              <div
                key={n.id}
                className={`${styles.item} ${!n.is_read ? styles.itemUnread : ''}`}
                onClick={() => !n.is_read && handleMarkRead(n.id)}
              >
                <div className={styles.itemHeader}>
                  <span className={`${styles.typeBadge} ${styles[`type_${n.notification_type}`] || ''}`}>
                    {TYPE_LABELS[n.notification_type] || n.notification_type}
                  </span>
                  <span className={styles.timestamp}>
                    {timeAgo(n.publish_at, t)}
                  </span>
                  <button
                    className={styles.dismissButton}
                    onClick={(e) => handleDismiss(e, n.id)}
                    title={t('notifications.dismiss')}
                  >
                    x
                  </button>
                </div>
                <div className={styles.itemTitle}>{n.localized_title}</div>
                <div className={styles.itemBody}>{n.localized_body}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
